import {
  Alert,
  Box,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Grid,
  Input,
  Table,
  Text,
  TextInput,
  Tooltip
} from '@mantine/core';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { Fragment, useEffect, useState } from 'react';
import { PiInfo } from 'react-icons/pi';
import { TbChevronDown } from 'react-icons/tb';
import { TbSearch } from 'react-icons/tb';
import { useCallback } from 'react';
import { getYearsForLastFiveYears } from '../../../../utils';
import { FilterInput } from './components/FilterInput';

export function groupFilesByWorkflow(files, quater, term, yearFilter) {
  const grouped = {};

  for (const file of files) {
    const workflowName = file.workflow_name;

    if (yearFilter && String(file.year) !== String(yearFilter)) continue;

    if (term) {
      if (workflowName.toLowerCase().includes(term.toLowerCase())) {
        if (quater) {
          if (file.period === quater) {
            const period = `${file.year} ${file.period}`;

            if (!grouped[workflowName]) {
              grouped[workflowName] = {};
            }

            if (!grouped[workflowName][period]) {
              grouped[workflowName][period] = [];
            }

            grouped[workflowName][period].push(file);
          }
        } else {
          const period = `${file.year} ${file.period}`;

          if (!grouped[workflowName]) {
            grouped[workflowName] = {};
          }

          if (!grouped[workflowName][period]) {
            grouped[workflowName][period] = [];
          }

          grouped[workflowName][period].push(file);
        }
      }
    } else {
      if (quater) {
        if (file.period === quater) {
          const period = `${file.year} ${file.period}`;

          if (!grouped[workflowName]) {
            grouped[workflowName] = {};
          }

          if (!grouped[workflowName][period]) {
            grouped[workflowName][period] = [];
          }

          grouped[workflowName][period].push(file);
        }
      } else {
        const period = `${file.year} ${file.period}`;

        if (!grouped[workflowName]) {
          grouped[workflowName] = {};
        }

        if (!grouped[workflowName][period]) {
          grouped[workflowName][period] = [];
        }

        grouped[workflowName][period].push(file);
      }
    }
  }

  return grouped;
}

const quaters = ['Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2', 'Y1', 'X1', 'X2'];
const lastFiveYears = getYearsForLastFiveYears();

export function SemiExtractionDrawer({
  workflowName,
  period,
  year,
  isOpen,
  isAutomation,
  onClose,
  replicateExtractionList,
  onReplicateAutoExtract,
  transformationProgressStatus
}) {
  const rootId = 'data-extraction-semi-extraction-drawer';
  const [list, setList] = useState({});
  const [quater, setQuater] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [term, setTerm] = useState('');
  const [openedWorflows, setOpenedWorflows] = useState([]);
  const [openedQuaters, setOpenedQuaters] = useState([]);
  const [selectedExtraction, setSelectedExtraction] = useState(null);
  const [applyTransformation, setApplyTransformation] = useState(false);

  const isTransformationStatusNotStarted = transformationProgressStatus === 'Not started';

  const handleExtractAndClose = useCallback(() => {
    onReplicateAutoExtract(selectedExtraction.id, !!applyTransformation);
    handleClose();
  }, [selectedExtraction?.id, applyTransformation, onReplicateAutoExtract]);

  const onSelectExtraction = value => {
    if (value.id === selectedExtraction?.id) {
      setApplyTransformation(null);
      setSelectedExtraction(null);
    } else {
      setSelectedExtraction(value);
      setApplyTransformation(null);
    }
  };

  useEffect(() => {
    if (!workflowName) return;

    setOpenedWorflows(prev => [...prev, workflowName]);
  }, [workflowName]);

  useEffect(() => {
    if (!period || !workflowName || !isOpen) return;

    function focusElement() {
      document.getElementById(workflowName)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setQuater(period);

    setYearFilter(String(year));

    setOpenedQuaters(prev => [...prev, `${workflowName}-${year} ${period}`]);

    const timeout = setTimeout(() => focusElement(), 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [period, workflowName, year, isOpen]);

  const onApplyTransformation = value => {
    setApplyTransformation(applyTransformation?.id === value.id ? null : value);
  };

  const handleClose = () => {
    setSelectedExtraction(null);
    setApplyTransformation(null);
    onClose();
  };

  useEffect(() => {
    if (replicateExtractionList?.data) {
      const _workflows = groupFilesByWorkflow(
        replicateExtractionList?.data,
        quater,
        term,
        yearFilter
      );
      setList(_workflows);
    }
  }, [replicateExtractionList?.data, quater, term, yearFilter]);

  const onSelectWorflow = key => {
    if (openedWorflows.includes(key)) {
      setOpenedWorflows(prev => prev.filter(val => val !== key));
    } else {
      setOpenedWorflows(prev => [...prev, key]);
    }
  };

  const onSelectQuater = key => {
    if (openedQuaters.includes(key)) {
      setOpenedQuaters(prev => prev.filter(val => val !== key));
    } else {
      setOpenedQuaters(prev => [...prev, key]);
    }
  };

  const onChangeQuater = value => {
    if (value === quater) {
      setQuater('');
    } else {
      setQuater(value);
    }
  };

  const onChangeYear = value => {
    if (value === yearFilter) {
      setYearFilter('');
    } else {
      setYearFilter(value);
    }
  };

  return (
    <Drawer
      id={rootId}
      title="Select Source"
      opened={isOpen}
      onClose={onClose}
      position="right"
      size="lg"
    >
      <Alert
        id={`${rootId}-alert`}
        variant="outline"
        color="blue"
        icon={<AiOutlineInfoCircle />}
        p="12px 16px"
      >
        For Clone Extraction, specify a reusable source that the system can use as a reference
        example.
      </Alert>
      <TextInput
        id={`${rootId}-search-input`}
        leftSection={<TbSearch fontSize="16px" color="#ADB5BD" />}
        placeholder="Search workflows"
        mt="16px"
        value={term}
        onChange={e => setTerm(e.target.value)}
      />

      <FilterInput
        idPrefix={rootId}
        label="Select Period"
        options={quaters}
        selected={quater}
        onSelect={onChangeQuater}
      />

      <FilterInput
        idPrefix={rootId}
        label="Select Year"
        options={lastFiveYears}
        selected={yearFilter}
        onSelect={onChangeYear}
      />

      <Box
        id={`${rootId}-list`}
        mt="50px"
        sx={{
          border: '1px solid #F1F3F5',
          borderRadius: '8px',
          maxHeight: 'calc(100vh - 465px)',
          overflow: 'auto'
        }}
      >
        {Object.keys(list).map(workflowName => (
          <Fragment key={workflowName}>
            <Flex
              sx={{
                borderBottom: '1px solid #F1F3F5',
                cursor: 'pointer'
              }}
              align="center"
              p="12px"
              fz="14px"
              fw="600"
              gap="8px"
              id={workflowName}
              onClick={() => onSelectWorflow(workflowName)}
            >
              <TbChevronDown fontSize="14px" color="#C1C2C5" />
              {workflowName}
            </Flex>
            {openedWorflows.includes(workflowName) &&
              Object.keys(list[workflowName]).map(quater => (
                <Fragment key={`${workflowName}-${quater}`}>
                  <Flex
                    id={`${rootId}-quarter-${workflowName}-${quater}`}
                    sx={{
                      borderBottom: '1px solid #F1F3F5',
                      cursor: 'pointer'
                    }}
                    align="center"
                    p="12px 12px 12px 28px"
                    fz="14px"
                    fw="600"
                    gap="8px"
                    onClick={() => onSelectQuater(`${workflowName}-${quater}`)}
                  >
                    <TbChevronDown fontSize="14px" color="#C1C2C5" />
                    {quater}
                  </Flex>
                  {openedQuaters.includes(`${workflowName}-${quater}`) && (
                    <Table
                      id={`${rootId}-table-${workflowName}-${quater}`}
                      withTableBorder
                      withColumnBorders
                      borderColor="gray.1"
                    >
                      <Table.Thead
                        id={`${rootId}-table-head-${workflowName}-${quater}`}
                        bg="gray.0"
                      >
                        <Table.Tr id={`${rootId}-table-head-row-${workflowName}-${quater}`}>
                          <Table.Th
                            id={`${rootId}-table-head-select-${workflowName}-${quater}`}
                          ></Table.Th>
                          <Table.Th
                            id={`${rootId}-table-head-file-${workflowName}-${quater}`}
                            c="#495057"
                            fz="12px"
                            fw="600"
                          >
                            File name
                          </Table.Th>
                          <Table.Th
                            id={`${rootId}-table-head-sheet-${workflowName}-${quater}`}
                            c="#495057"
                            fz="12px"
                            fw="600"
                          >
                            Sheet name
                          </Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody id={`${rootId}-table-body-${workflowName}-${quater}`}>
                        {Object.values(list[workflowName][quater])?.map(item => (
                          <Table.Tr
                            id={`${rootId}-table-row-${item.id}`}
                            onClick={() => {
                              onSelectExtraction(item);
                            }}
                            key={item.id}
                            bg={selectedExtraction?.id === item.id ? 'indigo.0' : '#fff'}
                          >
                            <Table.Td id={`${rootId}-table-select-${item.id}`} p="18px 12px">
                              <Checkbox
                                id={`${rootId}-checkbox-${item.id}`}
                                size="xs"
                                checked={selectedExtraction?.id === item.id}
                                onClick={() => onSelectExtraction(item)}
                              />
                            </Table.Td>
                            <Table.Td
                              id={`${rootId}-table-file-${item.id}`}
                              p="18px 12px"
                              fz="12px"
                              c="dark"
                            >
                              {item.file_name}
                            </Table.Td>
                            <Table.Td
                              id={`${rootId}-table-sheet-${item.id}`}
                              p="18px 12px"
                              fz="12px"
                              c="dark"
                            >
                              {item.sheet_name}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Fragment>
              ))}
          </Fragment>
        ))}
      </Box>

      <Flex id={`${rootId}-footer`} align="center" mt="48px" justify="space-between">
        <Button
          id={`${rootId}-cancel-button`}
          size="xs"
          onClick={handleClose}
          variant="default"
          color="dark"
        >
          Cancel
        </Button>
        <Flex id={`${rootId}-footer-actions`} align="center" gap="16px">
          <Flex id={`${rootId}-transform-toggle`} align="center" gap="8px">
            <Flex id={`${rootId}-transform-help`} align="center" gap="4px">
              {isAutomation && isTransformationStatusNotStarted ? (
                <Tooltip
                  id={`${rootId}-transform-tooltip-started`}
                  label={
                    <Text ta="right" fz="12px">
                      Transformation can be started once the automated
                      <br /> transformation process has begun.
                    </Text>
                  }
                  // color="white"
                >
                  <Button
                    id={`${rootId}-transform-info-button`}
                    size="xs"
                    variant="transparent"
                    p={0}
                  >
                    <PiInfo color="#228BE6" size={16} />
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip
                  id={`${rootId}-transform-tooltip-apply`}
                  label={
                    <Text ta="right" fz="12px">
                      Apply saved transformations along with <br /> extraction for consistent
                      results.
                    </Text>
                  }
                >
                  <Button
                    id={`${rootId}-transform-info-button`}
                    size="xs"
                    variant="transparent"
                    p={0}
                  >
                    <PiInfo color="#228BE6" size={16} />
                  </Button>
                </Tooltip>
              )}
              <Text
                id={`${rootId}-transform-label`}
                c={isTransformationStatusNotStarted ? 'gray.6' : 'dark.9'}
                fz="14px"
              >
                Also apply transformation
              </Text>
            </Flex>
            <Checkbox
              id={`${rootId}-transform-checkbox`}
              disabled={
                !selectedExtraction?.transformation_exists ||
                (isAutomation && isTransformationStatusNotStarted)
              }
              size="xs"
              onClick={() => onApplyTransformation(selectedExtraction)}
              checked={!!applyTransformation}
            />
          </Flex>
          <Button
            id={`${rootId}-extract-button`}
            disabled={!selectedExtraction}
            size="xs"
            onClick={handleExtractAndClose}
          >
            Extract
          </Button>
        </Flex>
      </Flex>
    </Drawer>
  );
}
